import { brand, neutral, fontSize, radius, shadow, table, monoFamily } from "app/theme/tokens";

/**
 * Global MUI component overrides.
 *
 * Behaviour the pages rely on is preserved from the previous overrides:
 * sticky table headers, no-wrap + ellipsis cells, the TableContainer height cap.
 * Only the look (colour, density, radius) changes.
 */
export const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: { backgroundColor: neutral[50], fontSize: fontSize.md },
      "*::-webkit-scrollbar": { width: 8, height: 8 },
      "*::-webkit-scrollbar-thumb": {
        backgroundColor: neutral[300],
        borderRadius: 8,
        border: `2px solid ${neutral[50]}`,
      },
      "*::-webkit-scrollbar-thumb:hover": { backgroundColor: neutral[400] },
      "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      code: { fontFamily: monoFamily },
    },
  },

  // ── Surfaces ──────────────────────────────────────────────────────────────
  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: { backgroundImage: "none" },
      outlined: { borderColor: neutral[200] },
      elevation1: { boxShadow: shadow.sm },
      elevation2: { boxShadow: shadow.sm },
      elevation3: { boxShadow: shadow.md },
    },
  },
  MuiCard: {
    defaultProps: { variant: "outlined" },
    styleOverrides: { root: { borderRadius: radius.lg, borderColor: neutral[200] } },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: { padding: "10px 14px" },
      title: { fontSize: fontSize.lg, fontWeight: 600 },
      subheader: { fontSize: fontSize.sm },
    },
  },
  MuiCardContent: {
    styleOverrides: { root: { padding: 14, "&:last-child": { paddingBottom: 14 } } },
  },
  MuiDivider: { styleOverrides: { root: { borderColor: neutral[200] } } },

  // ── Buttons ───────────────────────────────────────────────────────────────
  MuiButtonBase: { defaultProps: { disableRipple: false } },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        fontSize: fontSize.sm,
        fontWeight: 600,
        textTransform: "none",
        borderRadius: radius.md,
        padding: "4px 12px",
        minHeight: 30,
        minWidth: "auto",
        lineHeight: 1.4,
        boxShadow: "none",
        "&:hover": { boxShadow: "none" },
      },
      sizeSmall: { fontSize: fontSize.sm, padding: "2px 10px", minHeight: 26 },
      sizeLarge: { fontSize: fontSize.md, padding: "6px 16px", minHeight: 36 },
      outlined: { borderColor: neutral[300] },
      startIcon: { marginRight: 6, "& > *:nth-of-type(1)": { fontSize: 16 } },
      endIcon: { marginLeft: 6, "& > *:nth-of-type(1)": { fontSize: 16 } },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: radius.md },
      sizeSmall: { padding: 4, "& svg": { fontSize: 18 } },
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontSize: fontSize.sm,
        padding: "3px 10px",
        "&.Mui-selected": { backgroundColor: brand.tealSoft, color: brand.tealDeep },
      },
    },
  },
  MuiFab: { styleOverrides: { root: { boxShadow: shadow.md } } },

  // ── Inputs ────────────────────────────────────────────────────────────────
  MuiTextField: { defaultProps: { size: "small" } },
  MuiFormControl: { defaultProps: { size: "small" } },

  // ── Date pickers: no future dates anywhere by default ─────────────────────
  // Future days/months are greyed out in the calendar and a typed future date
  // shows as an error. A page that genuinely needs the future (e.g. Holiday
  // Calendar) opts out with disableFuture={false}.
  MuiDatePicker: { defaultProps: { disableFuture: true } },
  MuiDesktopDatePicker: { defaultProps: { disableFuture: true } },
  MuiMobileDatePicker: { defaultProps: { disableFuture: true } },
  MuiStaticDatePicker: { defaultProps: { disableFuture: true } },
  // ── Dates are picked from the calendar only — no typing ───────────────────
  // The date box inside every DatePicker / DesktopDatePicker is read-only (the
  // calendar button still works), and clicking the box opens the calendar too.
  // A picker that passes its own readOnly / onClick on the field keeps its own.
  MuiDateField: {
    defaultProps: {
      readOnly: true,
      // (the handler sits on the field's outer box; a click on the calendar button
      // itself is left alone so it isn't opened twice)
      onClick: (e) => {
        if (e.target.closest(".MuiInputAdornment-root")) return;
        e.currentTarget.querySelector(".MuiInputAdornment-root button:not(:disabled)")?.click();
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: { size: "small" },
    styleOverrides: {
      paper: { boxShadow: shadow.lg, border: `1px solid ${neutral[200]}` },
      option: { fontSize: fontSize.sm, minHeight: "32px !important" },
      tag: { margin: 2 },
    },
  },
  MuiInputBase: {
    styleOverrides: { root: { fontSize: fontSize.md }, input: { "&::placeholder": { color: neutral[400], opacity: 1 } } },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: radius.md,
        backgroundColor: neutral[0],
        "& .MuiOutlinedInput-notchedOutline": { borderColor: neutral[300] },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: neutral[400] },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand.tealDark, borderWidth: 1.5 },
        "&.Mui-disabled": { backgroundColor: neutral[50] },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: { root: { fontSize: fontSize.md, "&.Mui-focused": { color: brand.tealDark } } },
  },
  MuiFormLabel: { styleOverrides: { root: { fontSize: fontSize.sm, color: neutral[600] } } },
  MuiFormHelperText: { styleOverrides: { root: { fontSize: fontSize.xs, marginLeft: 2 } } },
  MuiSelect: { defaultProps: { size: "small" } },
  MuiMenu: {
    styleOverrides: { paper: { boxShadow: shadow.lg, border: `1px solid ${neutral[200]}`, borderRadius: radius.lg } },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: fontSize.sm,
        minHeight: 32,
        "&.Mui-selected": { backgroundColor: brand.tealSoft },
        "&.Mui-selected:hover": { backgroundColor: brand.tealSoft },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: { root: { padding: 4, "& svg": { fontSize: 18 } } },
  },
  MuiRadio: { styleOverrides: { root: { padding: 4, "& svg": { fontSize: 18 } } } },
  MuiSwitch: { defaultProps: { size: "small" } },

  // ── Feedback ──────────────────────────────────────────────────────────────
  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: fontSize.xs,
        fontWeight: 600,
        borderRadius: radius.pill,
        height: 22,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
      sizeSmall: { height: 20 },
    },
  },
  MuiAlert: {
    styleOverrides: { root: { borderRadius: radius.md, fontSize: fontSize.sm, padding: "4px 12px" } },
  },
  MuiTooltip: {
    defaultProps: { arrow: true },
    styleOverrides: {
      tooltip: { fontSize: fontSize.xs, backgroundColor: neutral[800], padding: "4px 8px", borderRadius: radius.sm },
      arrow: { color: neutral[800] },
    },
  },
  MuiLinearProgress: { styleOverrides: { root: { borderRadius: 2, height: 3 } } },
  MuiSkeleton: { styleOverrides: { root: { backgroundColor: neutral[100] } } },

  // ── Dialogs & drawers ─────────────────────────────────────────────────────
  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: radius.lg, boxShadow: shadow.lg },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: fontSize.lg,
        fontWeight: 600,
        padding: "12px 16px",
        borderBottom: `1px solid ${neutral[200]}`,
      },
    },
  },
  MuiDialogContent: { styleOverrides: { root: { padding: "12px 16px" } } },
  MuiDialogActions: {
    styleOverrides: {
      root: { padding: "10px 16px", borderTop: `1px solid ${neutral[200]}`, gap: 8, "& > :not(style) ~ :not(style)": { marginLeft: 0 } },
    },
  },
  MuiDrawer: { styleOverrides: { paper: { borderColor: neutral[200] } } },
  MuiBackdrop: {
    styleOverrides: { root: { "&:not(.MuiBackdrop-invisible)": { backgroundColor: "rgba(15, 23, 42, 0.45)" } } },
  },

  // ── Navigation ────────────────────────────────────────────────────────────
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 36, borderBottom: `1px solid ${neutral[200]}` },
      indicator: { height: 2, borderRadius: 2 },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        minHeight: 36,
        padding: "6px 14px",
        fontSize: fontSize.sm,
        fontWeight: 600,
        textTransform: "none",
        color: neutral[600],
      },
    },
  },
  MuiBreadcrumbs: { styleOverrides: { root: { fontSize: fontSize.sm } } },
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: 36,
        paddingTop: 4,
        paddingBottom: 4,
        "& .MuiIconButton-root": { padding: 4 },
      },
    },
  },

  // ── Tables (MUI <Table>) ───────────────────────────────────────────────────
  MuiTableContainer: {
    styleOverrides: {
      root: {
        border: `1px solid ${table.border}`,
        borderRadius: radius.lg,
        backgroundColor: neutral[0],
        maxWidth: "100%",
        maxHeight: "calc(100vh - 163px)",
        overflow: "auto",
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: { tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, width: "100%" },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        "&:nth-of-type(even) > .MuiTableCell-body": { backgroundColor: table.stripe },
        "&:hover > .MuiTableCell-body": { backgroundColor: table.hover },
        "&.Mui-selected > .MuiTableCell-body, &.Mui-selected:hover > .MuiTableCell-body": {
          backgroundColor: table.selected,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        fontSize: fontSize.xs + 1,
        padding: "4px 10px",
        color: neutral[800],
        borderBottom: `1px solid ${neutral[100]}`,
        maxWidth: 160,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        backgroundColor: neutral[0],
      },
      head: {
        fontSize: fontSize.xs,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        padding: "6px 10px",
        color: table.headerText,
        backgroundColor: table.headerBg,
        borderBottom: `1px solid ${table.border}`,
        position: "sticky",
        top: 0,
        zIndex: 10,
        lineHeight: 1.3,
      },
      sizeSmall: { padding: "3px 10px" },
      paddingCheckbox: { width: 36, padding: "0 4px" },
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        "& svg": { marginLeft: 2, fontSize: 14 },
        "&.Mui-active": { color: brand.tealDeep },
        "&.Mui-active svg": { color: `${brand.tealDark} !important` },
      },
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      root: { borderTop: `1px solid ${neutral[200]}`, overflow: "hidden" },
      toolbar: { minHeight: "36px !important", paddingLeft: 8, paddingRight: 8 },
      selectLabel: { fontSize: fontSize.xs, color: neutral[500] },
      displayedRows: { fontSize: fontSize.xs, color: neutral[600] },
      select: { fontSize: fontSize.xs, paddingTop: 2, paddingBottom: 2 },
      actions: { marginLeft: 8, "& .MuiIconButton-root": { padding: 4 } },
    },
  },
};

export default components;
